import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  FileText, 
  Plus, 
  Calendar,
  Building,
  TrendingUp,
  Share2,
  Trash2,
  Loader2,
  AlertCircle,
  Eye,
  BarChart3,
  Search,
  X,
  User
} from "lucide-react";
import Navigation from "@/components/Navigation";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const ConsumptionReportsListPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter reports based on search term
  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    
    const search = searchTerm.toLowerCase();
    return reports.filter(report => 
      (report.client_name && report.client_name.toLowerCase().includes(search)) ||
      (report.building_name && report.building_name.toLowerCase().includes(search)) ||
      (report.pdl && report.pdl.toLowerCase().includes(search)) ||
      (report.period && report.period.toLowerCase().includes(search))
    );
  }, [reports, searchTerm]);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/consumption-reports`, {
        headers: getAuthHeader(),
      });
      
      if (response.data.success) {
        setReports(response.data.reports);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Erreur', {
        description: 'Impossible de charger les rapports de consommation.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shareToken) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      return;
    }

    setDeleting(shareToken);
    try {
      const response = await axios.delete(
        `${API}/api/consumption-reports/${shareToken}`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success('Rapport supprimé', {
          description: 'Le rapport a été supprimé avec succès.',
        });
        loadReports();
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Erreur', {
        description: 'Impossible de supprimer le rapport.',
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleShare = (report) => {
    const isMultisite = report.report_type === 'consumption_multisites';
    const shareUrl = isMultisite 
      ? `${window.location.origin}/consumption/multisites/${report.share_token}`
      : `${window.location.origin}/consumption/report/${report.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Lien copié !', {
      description: 'Le lien de partage a été copié dans le presse-papiers.',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
        <Navigation />
        <div className="container mx-auto px-4 py-20 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des rapports...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Rapports de Consommation</h1>
            <p className="text-sm text-muted-foreground">
              Gérez vos rapports de consommation énergétique
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="outline" size="lg" data-testid="back-to-dashboard-btn">
                <BarChart3 className="w-5 h-5 mr-2" />
                Tableau de bord
              </Button>
            </Link>
            <Link to="/consumption/upload">
              <Button size="lg">
                <Plus className="w-5 h-5 mr-2" />
                Nouveau rapport
              </Button>
            </Link>
          </div>
        </div>

        {/* Search Bar */}
        {reports.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom de client, bâtiment, PDL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-reports"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Reports count */}
        {reports.length > 0 && (
          <p className="text-sm text-muted-foreground mb-4">
            {filteredReports.length} rapport{filteredReports.length !== 1 ? 's' : ''} 
            {searchTerm && ` trouvé${filteredReports.length !== 1 ? 's' : ''}`}
            {searchTerm && filteredReports.length !== reports.length && ` sur ${reports.length}`}
          </p>
        )}

        {/* Reports List */}
        {reports.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 mx-auto mb-4 bg-accent rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Aucun rapport</h3>
              <p className="text-muted-foreground mb-6">
                Commencez par uploader votre premier rapport de consommation
              </p>
              <Link to="/consumption/upload">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Charger un rapport
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : filteredReports.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 mx-auto mb-4 bg-accent rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Aucun résultat</h3>
              <p className="text-muted-foreground mb-4">
                Aucun rapport ne correspond à "{searchTerm}"
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Effacer la recherche
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredReports.map((report) => (
              <Card key={report.share_token} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                          <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          {/* Client Name - Primary display */}
                          <h3 className="text-lg sm:text-xl font-semibold truncate">
                            {report.client_name || report.building_name || 'Rapport de consommation'}
                          </h3>
                          {/* Building name as subtitle if different from client_name */}
                          {report.client_name && report.building_name && report.client_name !== report.building_name && (
                            <p className="text-sm text-muted-foreground truncate">{report.building_name}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {report.pdl && (
                              <Badge variant="secondary" className="text-xs">
                                PDL: {report.pdl}
                              </Badge>
                            )}
                            {report.segment && (
                              <Badge variant="outline" className="text-xs">
                                {report.segment}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{report.period || '12 derniers mois'}</span>
                        </div>
                        {report.totals && (
                          <div className="flex items-center gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-primary" />
                            {(() => {
                              // Calculate YTD based on measured data (same logic as report view pages)
                              const monthlyData = report.monthly_data?.current_year || {};
                              const monthNamesLower = ['', 'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 
                                                       'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
                              
                              // Determine report month using same logic as frontend:
                              // 1. report.report_month_num (explicit number)
                              // 2. Parse from report.report_month string (e.g., "Janvier 2026")
                              // 3. Parse from last_month.name (for monosite reports)
                              // 4. Extract from report_date (format: DD/MM/YYYY)
                              // 5. Find last month with MEASURED data
                              let reportMonthNum = parseInt(report.report_month_num || '0');
                              
                              // Try to parse from report.report_month string
                              if (reportMonthNum === 0 && report.report_month) {
                                const monthStr = report.report_month.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(' ')[0];
                                const foundIndex = monthNamesLower.findIndex(m => m && monthStr.startsWith(m.substring(0, 3)));
                                if (foundIndex > 0) {
                                  reportMonthNum = foundIndex;
                                }
                              }
                              
                              // For monosite: use last_month.name and add 1 (report is generated month after last_month)
                              if (reportMonthNum === 0 && report.last_month?.name) {
                                const monthStr = report.last_month.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                const foundIndex = monthNamesLower.findIndex(m => m && monthStr.startsWith(m.substring(0, 3)));
                                if (foundIndex > 0) {
                                  // Report is generated the month AFTER last_month, so add 1
                                  reportMonthNum = foundIndex + 1;
                                  if (reportMonthNum > 12) reportMonthNum = 12;
                                }
                              }
                              
                              // For monosite: extract from report_date (format: DD/MM/YYYY)
                              if (reportMonthNum === 0 && report.report_date) {
                                const parts = report.report_date.split('/');
                                if (parts.length >= 2) {
                                  reportMonthNum = parseInt(parts[1]) || 0;
                                }
                              }
                              
                              // Fallback: find last month with MEASURED data (for multisites)
                              if (reportMonthNum === 0) {
                                const monthsWithMeasuredData = Object.entries(monthlyData)
                                  .filter(([, data]) => (data?.measured_mwh || 0) > 0)
                                  .map(([key]) => parseInt(key))
                                  .sort((a, b) => a - b);
                                
                                if (monthsWithMeasuredData.length > 0) {
                                  reportMonthNum = Math.max(...monthsWithMeasuredData);
                                } else {
                                  // Last resort: use current month
                                  reportMonthNum = new Date().getMonth() + 1;
                                }
                              }
                              
                              // Calculate YTD: sum data up to reportMonthNum only
                              let ytdCurrent = 0;
                              let ytdPrevious = 0;
                              
                              for (let m = 1; m <= reportMonthNum; m++) {
                                const monthKey = m.toString().padStart(2, '0');
                                const monthData = monthlyData[monthKey] || {};
                                const measuredValue = Number(monthData.measured_mwh || 0);
                                const consumptionValue = Number(monthData.consumption_mwh || 0);
                                const estimatedValue = Number(monthData.estimated_mwh || 0);
                                const previousValue = Number(monthData.previous_year_mwh || 0);
                                
                                // Priority: measured > consumption > estimated
                                // For monosite: use consumption_mwh (no measured_mwh field)
                                // For multisites: use measured_mwh first
                                if (measuredValue > 0) {
                                  ytdCurrent += measuredValue;
                                  ytdPrevious += previousValue;
                                } else if (consumptionValue > 0 && estimatedValue === 0) {
                                  // This is likely a monosite report with only consumption_mwh
                                  ytdCurrent += consumptionValue;
                                  ytdPrevious += previousValue;
                                } else if (estimatedValue > 0 && m <= reportMonthNum) {
                                  ytdCurrent += estimatedValue;
                                  ytdPrevious += previousValue;
                                }
                              }
                              
                              // Fallback to totals if no monthly data
                              if (ytdCurrent === 0) {
                                ytdCurrent = report.totals.current_year_mwh || 0;
                                ytdPrevious = report.totals.previous_year_mwh || 0;
                              }
                              
                              const diffPercent = ytdPrevious > 0 
                                ? ((ytdCurrent - ytdPrevious) / ytdPrevious * 100).toFixed(1)
                                : report.totals.diff_percent || 0;
                              
                              return (
                                <>
                                  <span className="font-semibold">{ytdCurrent.toFixed(1)} MWh</span>
                                  <span className={`text-xs ${parseFloat(diffPercent) < 0 ? 'text-success' : 'text-destructive'}`}>
                                    {parseFloat(diffPercent) > 0 ? '+' : ''}{diffPercent}%
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        {report.subscribed_power_kw && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Building className="w-4 h-4" />
                            <span>{report.subscribed_power_kw} kW souscrit</span>
                          </div>
                        )}
                      </div>

                      {report.upload_date && (
                        <p className="text-xs text-muted-foreground mt-3">
                          Uploadé le{' '}
                          {format(new Date(report.upload_date), 'dd MMMM yyyy à HH:mm', {
                            locale: fr,
                          })}
                        </p>
                      )}
                      
                      {/* View counter badge */}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-medium">
                            {report.view_count || 0} vue{(report.view_count || 0) !== 1 ? 's' : ''}
                          </span>
                          {report.last_viewed_at && (
                            <span className="text-muted-foreground">
                              • Dernière consultation : {format(new Date(report.last_viewed_at), 'dd/MM à HH:mm', { locale: fr })}
                            </span>
                          )}
                        </div>
                        {report.report_type === 'consumption_multisites' && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            <BarChart3 className="w-3 h-3 mr-1" />
                            Multisites
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => {
                          const isMultisite = report.report_type === 'consumption_multisites';
                          navigate(isMultisite 
                            ? `/consumption/multisites/${report.share_token}`
                            : `/consumption/report/${report.share_token}`
                          );
                        }}
                      >
                        Voir le rapport
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShare(report)}
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Partager
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(report.share_token)}
                        disabled={deleting === report.share_token}
                      >
                        {deleting === report.share_token ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumptionReportsListPage;
